import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Tabs, Tab, Avatar, Snackbar, Alert,
  IconButton, Grid, CircularProgress
} from '@mui/material';
import { PhotoCamera, Close } from '@mui/icons-material';
import api from '../utils/api';

interface UserProfileModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ open, onClose, onUpdate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [profile, setProfile] = useState({ email: '', avatar_url: '', max_id: '' });
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState<string[]>([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      api.get('/users/me').then(res => {
        setProfile({
          email: res.data.email || '',
          avatar_url: res.data.avatar_url || '',
          max_id: res.data.max_id || ''
        });
      }).catch(err => console.error('Ошибка загрузки профиля:', err));
    }
  }, [open]);

  const handleSaveProfile = async () => {
    try {
      await api.put('/users/me', profile);
      setSnackbar({ open: true, message: 'Профиль обновлен', severity: 'success' });
      onUpdate();
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка обновления', severity: 'error' });
    }
  };

  const handleChangePassword = async () => {
    try {
      await api.put('/users/me/password', passwords);
      setSnackbar({ open: true, message: 'Пароль изменен', severity: 'success' });
      setPasswords({ oldPassword: '', newPassword: '' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Ошибка смены пароля', severity: 'error' });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile({ ...profile, avatar_url: res.data.url });
      setSnackbar({ open: true, message: 'Фото загружено', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка загрузки фото', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const loadGallery = async () => {
    setLoadingGallery(true);
    try {
      const res = await api.get('/photos/list');
      setGallery(res.data);
      setGalleryOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка загрузки галереи', severity: 'error' });
    } finally {
      setLoadingGallery(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Профиль пользователя</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Avatar src={profile.avatar_url} sx={{ width: 100, height: 100, mb: 2 }}>А</Avatar>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <IconButton 
                color="primary" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Загрузить с компьютера"
              >
                {uploading ? <CircularProgress size={24} /> : <PhotoCamera />}
              </IconButton>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={loadGallery}
                disabled={loadingGallery}
              >
                Выбрать из галереи
              </Button>
            </Box>
          </Box>

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Личные данные" />
            <Tab label="Безопасность" />
          </Tabs>

          {tabValue === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Email" value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})} fullWidth />
              <TextField label="URL аватара" value={profile.avatar_url} onChange={e => setProfile({...profile, avatar_url: e.target.value})} fullWidth />
              <TextField label="ID в MAX" value={profile.max_id} onChange={e => setProfile({...profile, max_id: e.target.value})} fullWidth />
              <Button variant="contained" onClick={handleSaveProfile}>Сохранить</Button>
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Старый пароль" type="password" value={passwords.oldPassword} onChange={e => setPasswords({...passwords, oldPassword: e.target.value})} fullWidth />
              <TextField label="Новый пароль" type="password" value={passwords.newPassword} onChange={e => setPasswords({...passwords, newPassword: e.target.value})} fullWidth />
              <Button variant="contained" onClick={handleChangePassword}>Сменить пароль</Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрыть</Button>
        </DialogActions>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({...snackbar, open: false})}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Dialog>

      <Dialog open={galleryOpen} onClose={() => setGalleryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Выберите фото
          <IconButton onClick={() => setGalleryOpen(false)} sx={{ float: 'right' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {gallery.length === 0 ? (
            <Typography color="textSecondary">Нет загруженных фото</Typography>
          ) : (
            <Grid container spacing={2}>
              {gallery.map((url, idx) => (
                <Grid size={{ xs: 4, sm: 3 }} key={idx}>
                  <Box
                    component="img"
                    src={url}
                    sx={{ 
                      width: '100%', 
                      height: 100, 
                      objectFit: 'cover', 
                      borderRadius: 2, 
                      cursor: 'pointer',
                      border: profile.avatar_url === url ? '3px solid #1976d2' : '1px solid #ddd'
                    }}
                    onClick={() => {
                      setProfile({ ...profile, avatar_url: url });
                      setGalleryOpen(false);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserProfileModal;
